import { ContentDownload } from '@fs/zion-icon'
import MenuSearch from '@fs/zion-icon/dist/cjs/icons/MenuSearch'
import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import { Button, Row, TextField, H5 } from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import usePrivatePersons from '../../../hooks/usePrivatePersons'
import { downloadFile } from '../../../util/FileDownloadUtil'
import { getPrivatePersons } from '../../../services/userService'
import { UserContext } from '../UserContext'
import PrivatePersonsTable from './PrivatePersonsTable'
import { getBirthDate, getBirthPlace, getDeathDate, getDeathPlace, getName } from './PrivatePersonsUtil'
import DmcPaginator from '../../common/DmcPaginator'

export default function PrivatePersons() {
  usePageView({ page_detail: 'DMConsole:PrivatePersons' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.privatePersons'))
  const { currentUser } = useContext(UserContext)
  const { error, ok } = useNotifications()
  const cisId = currentUser.dmcUser.cisId
  const MAX_RESULTS = 100
  const [index, setIndex] = useState(0)
  const [pageCount, setPageCount] = useState(20)
  const [nameTextField, setNameTextField] = useState()
  const { persons, totalPersonsInSpace, nameFilter, setNameFilter } = usePrivatePersons(cisId, pageCount, index)
  const hideOption10 = css`
    option[value='10'] {
      display: none;
    }
  `

  useEffect(() => {
    window.scrollTo({ top: 0, insetInlineStart: 0, behavior: 'smooth' })
  }, [index])

  const downloadPrivatePersons = () => {
    if (cisId !== null) {
      getPrivatePersons(cisId, null, 'all')
        .then((response) => {
          const stringList = [
            `${t('personId', 'Person ID')},${t('name', 'Name')},${t('birthDate', 'Birth Date')},${t(
              'birthPlace',
              'Birth Place'
            )},${t('deathDate', 'Death Date')},${t('deathPlace', 'Death Place')}`,
          ]
          response.data.persons.forEach((person) => {
            stringList.push(
              `${person.id},${getName(person)},${getBirthDate(person)},${getBirthPlace(person)},${getDeathDate(
                person,
                t
              )},${getDeathPlace(person)}`
            )
          })
          const privatePersonsString = stringList.join('\n')
          downloadFile(privatePersonsString, `${cisId} privatePersons.csv`)
        })
        .then(() => {
          ok(t('action.downloadCompleted', 'Download Completed'))
        })
        .catch((err) => {
          error(
            t('user.errors.gettingPrivatePersons', {
              defaultValue: `Error getting all private persons: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    }
  }

  const nameFilterOnChange = (e) => {
    setNameTextField(e.target.value)
    if (e.target.value === '') {
      setNameFilter(null)
    }
  }

  const nameFilterOnEnterKey = (e) => {
    setNameFilter(e.target.value)
  }

  if (!persons) {
    return null
  }
  return (
    <>
      {totalPersonsInSpace === 0 ? (
        <H5 supplementary>{t('user.privatePersons.noPersonsFound', 'No persons found')}</H5>
      ) : (
        <>
          <Row>
            <TextField
              placeholder={t('user.privatePersons.nameFilterPlaceholder', 'Search by name')}
              onChange={nameFilterOnChange}
              value={nameTextField}
              type="text"
              Icon={MenuSearch}
              onEnterKey={nameFilterOnEnterKey}
              growX
            />
            <Button id="downloadBtn" emphasis="medium" onClick={downloadPrivatePersons} Icon={ContentDownload}>
              {t('action.download', 'Download')}
            </Button>
          </Row>
          {!nameFilter && totalPersonsInSpace ? (
            <div css={hideOption10}>
              <DmcPaginator
                maxPageCount={MAX_RESULTS}
                length={totalPersonsInSpace}
                pageCount={pageCount}
                setPageCount={setPageCount}
                index={index}
                setIndex={setIndex}
              >
                <PrivatePersonsTable persons={persons} />
              </DmcPaginator>
            </div>
          ) : (
            <PrivatePersonsTable persons={persons} />
          )}
        </>
      )}
    </>
  )
}
